import './sparkleBurst.css'

import { cn } from '@/lib/utils'

/** Reserved whether or not anything is running, so nothing below it moves. */
const STRIP_HEIGHT = 40

/** Exit timing, which the caller has to wait out before clearing the run. */
export const BACKSTAGE_EXIT_MS = 1000

/**
 * What the agent is doing while the customer waits. Every line is mounted and
 * stacked, so one fades up through the one it replaces rather than waiting for
 * a blank strip — only the first arrives from under the nav, and only the last
 * leaves that way.
 *
 * Opacity is transitioned rather than animated: a keyframe would have to finish
 * before the next could start, which is what made the hand-off blink.
 */
export function BackstageStrip({
  items,
  current,
  exiting,
}: {
  /** Each line is a function of whether it is showing, so counters start on cue. */
  items: ((active: boolean) => React.ReactNode)[]
  /** Which line is showing, or -1 when nothing is running. */
  current: number
  exiting?: boolean
}) {
  return (
    <div className="relative shrink-0 overflow-hidden" style={{ height: STRIP_HEIGHT }} aria-live="polite">
      {items.map((item, index) => {
        const active = index === current && !exiting
        const leaving = index === current && exiting
        // Only the opening line drops in; the rest are already in place, waiting.
        const waiting = index > current && index === 0

        return (
          <span
            key={index}
            aria-hidden={!active}
            className={cn(
              'text-ink-300 absolute inset-0 flex items-center justify-end gap-2 px-4 text-xs',
              // Leaving runs on its own curve; the others transition so an
              // incoming line can rise through the one it replaces.
              leaving
                ? 'cai-backstage-leave'
                : cn(
                    'transition-[opacity,transform] duration-700 ease-out',
                    active
                      ? 'translate-y-0 opacity-100'
                      : waiting
                        ? '-translate-y-full opacity-0'
                        : 'opacity-0'
                  )
            )}
          >
            {item(index === current)}
          </span>
        )
      })}
    </div>
  )
}
